import { useState, useEffect, useCallback } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { DEFAULT_PAGE_INDEX, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { PlayArrow } from '@mui/icons-material';
import { AutojobResponse } from 'src/modules/dashboard/models/autojobResponse';
import { AutojobSearchFilter } from 'src/modules/dashboard/models/autojobsSearchFilter';
import { getScheduleLabel, getScheduleTypeLabel, getTriggerLabel } from 'src/shared/functions/autojobHelpers';
import { PagedResult } from 'src/shared/models/pagedResult';
import { AxiosResponse } from 'axios';

type AutojobRunningDataGridProps = {
  fetchFn: (options: AutojobSearchFilter) => Promise<AxiosResponse<PagedResult<AutojobResponse>>>;
  searchString: string;
  title: string;
};

export const AutojobRunningDataGrid = (props: AutojobRunningDataGridProps) => {
  const { fetchFn, searchString, title } = props;
  const { t } = useTranslation(['dashboard', 'settings']);
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const [rows, setRows] = useState<AutojobResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: 10,
    page: DEFAULT_PAGE_INDEX,
  });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'executedTimeStamp', sort: 'desc' }]);

  const updateData = useCallback((k: string, v: unknown) => setData((prev) => ({ ...prev, [k]: v })), []);

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const result = await fetchFn({
        status: 2,
        filter: searchString,
        pageIndex: data.page,
        pageSize: data.pageSize,
        sortCol: sortModel[0].field,
        sortDir: sortModel[0].sort ?? 'desc',
      });
      const items = extendObjectWithId(result.data.results, 'task_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setRows(items);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, searchString, data.page, data.pageSize, sortModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: () => (
        <Tooltip title={t('settings:autojobs.statusTabs.running')} arrow placement='left'>
          <PlayArrow color='info' sx={{ fontSize: 20 }} />
        </Tooltip>
      ),
    },
    { field: 'taskName', headerName: t('settings:autojobs.name'), flex: 1.5 },
    {
      field: 'scheduleType',
      headerName: t('settings:autojobs.scheduleType'),
      flex: 0.8,
      sortable: false,
      renderCell: (params: GridCellParams) => getScheduleTypeLabel(params.row.scheduleType, t),
    },
    {
      field: 'interval',
      headerName: t('dashboard:autojobs.interval'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams) => getScheduleLabel(params.row.increaseNum, params.row.scheduleType, t),
    },
    {
      field: 'trigger',
      headerName: t('dashboard:autojobs.trigger'),
      flex: 1.2,
      sortable: false,
      renderCell: (params: GridCellParams) => getTriggerLabel(params.row.custom2, t),
    },
    {
      field: 'executedTimeStamp',
      headerName: t('settings:autojobs.started'),
      flex: 1,
      renderCell: (params: GridCellParams) => formatDateAndTime(params.row.executedTimeStamp),
    },
  ];

  if (!data.loading && data.totalRows === 0) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <Typography variant='subtitle1' sx={{ px: 2, pt: 2, pb: 1 }}>
        {title}
      </Typography>
      <DataGrid<AutojobResponse>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
    </Box>
  );
};
